import React, { useState, Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'next-themes';
import { cn } from '@/lib/utils';
import Sidebar from '@/components/layout/Sidebar';
import Header from '@/components/layout/Header';
import Dashboard from '@/components/dashboard/Dashboard';
import ExecutiveDashboard from '@/components/dashboard/ExecutiveDashboard';
import SmartInbox from '@/components/inbox/SmartInbox';
import ActionHub from '@/components/actions/ActionHub';
import MeetingHub from '@/components/meetings/MeetingHub';
import CalendarView from '@/components/calendar/CalendarView';
import StrategyView from '@/components/strategy/StrategyView';
import ProjectsView from '@/components/projects/ProjectsView';
import TeamView from '@/components/team/TeamView';
import DocumentsView from '@/components/documents/DocumentsView';
import DocumentIntelligencePanel from '@/components/documents/DocumentIntelligencePanel';
import StakeholderView from '@/components/stakeholders/StakeholderView';
import ActivityView from '@/components/activity/ActivityView';
import ReportsView from '@/components/reports/ReportsView';
import WeeklyDigestView from '@/components/digest/WeeklyDigestView';
import AIControlsView from '@/components/ai/AIControlsView';
import HITLGovernanceView from '@/components/governance/HITLGovernanceView';
import AuditTrailView from '@/components/governance/AuditTrailView';
import ActionsRequired from '@/components/governance/ActionsRequired';
import { SignalEngineView } from '@/components/signals/SignalEngineView';
import RiskPredictionView from '@/components/risk/RiskPredictionView';
import ReleaseIntelligenceView from '@/components/engineering/ReleaseIntelligenceView';
import DecisionLog from '@/components/decisions/DecisionLog';
import { IssueRegister } from '@/components/issues/IssueRegister';
import { KnowledgeBase } from '@/components/knowledge/KnowledgeBase';
import { BrandingView } from '@/components/branding/BrandingView';
import { PortfolioView } from '@/components/portfolio/PortfolioView';
import { ComplianceCenter } from '@/components/compliance/ComplianceCenter';
import { ProtectedFeature } from '@/components/auth/ProtectedFeature';
import AIChatButton from '@/components/chat/AIChatButton';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePresence } from '@/hooks/usePresence';
import { useActivityTracking } from '@/hooks/useActivityTracking';
import { useUserRole } from '@/hooks/useUserRole';

const Index = () => {
  const [searchParams] = useSearchParams();
  // Supports deep-linking into a specific view via ?view=<key> (e.g. from
  // the meeting recorder browser extension linking straight to Meeting Hub
  // after a submission) — read once on mount as the initial view, not kept
  // in sync afterward. An unknown/absent value falls back to 'dashboard'
  // same as before; renderContent()'s switch already has a default case
  // for any unrecognized activeView.
  const [activeView, setActiveView] = useState(() => searchParams.get('view') || 'dashboard');
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const { onlineUsers } = usePresence(activeView);
  const { trackAction } = useActivityTracking();
  const { role } = useUserRole();

  const isExecutive = role === 'executive';

  const handleViewChange = (view: string) => {
    setActiveView(view);
    trackAction({ actionType: 'feature_usage', actionDetails: { view, feature: 'navigation' } });
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        // Show Executive Dashboard for executive role
        return isExecutive ? (
          <ExecutiveDashboard />
        ) : (
          <Dashboard onNavigateToSignals={() => setActiveView('governance')} />
        );
      case 'inbox':
        return <SmartInbox />;
      case 'actionHub':
        return (
          <ProtectedFeature feature="actionHub">
            <ActionHub onNavigateToGovernance={() => setActiveView('actionsRequired')} />
          </ProtectedFeature>
        );
      case 'calendar':
        return <CalendarView />;
      case 'meetings':
        return (
          <ProtectedFeature feature="meetings">
            <MeetingHub onNavigateToActionHub={() => setActiveView('actionHub')} />
          </ProtectedFeature>
        );
      case 'projects':
        return (
          <ProtectedFeature feature="projects">
            <ProjectsView />
          </ProtectedFeature>
        );
      case 'documents':
        return <DocumentsView onNavigateToDocumentIntelligence={() => setActiveView('documentIntelligence')} />;
      case 'documentIntelligence':
        return (
          <ProtectedFeature feature="documentIntelligence">
            <DocumentIntelligencePanel />
          </ProtectedFeature>
        );
      case 'stakeholders':
        return (
          <ProtectedFeature feature="stakeholders">
            <StakeholderView />
          </ProtectedFeature>
        );
      case 'reports':
        return (
          <ProtectedFeature feature="reports">
            <ReportsView />
          </ProtectedFeature>
        );
      case 'weeklyDigest':
        return (
          <ProtectedFeature feature="weeklyDigest">
            <WeeklyDigestView />
          </ProtectedFeature>
        );
      case 'portfolio':
        return (
          <ProtectedFeature feature="portfolio">
            <PortfolioView />
          </ProtectedFeature>
        );
      case 'compliance':
        return (
          <ProtectedFeature feature="compliance">
            <ComplianceCenter />
          </ProtectedFeature>
        );
      case 'activity':
        return (
          <ProtectedFeature feature="activity">
            <ActivityView />
          </ProtectedFeature>
        );
      case 'strategy':
        return (
          <ProtectedFeature feature="strategy">
            <StrategyView />
          </ProtectedFeature>
        );
      case 'team':
        return (
          <ProtectedFeature feature="team">
            <TeamView />
          </ProtectedFeature>
        );
      case 'knowledge':
        return (
          <ProtectedFeature feature="knowledge">
            <KnowledgeBase />
          </ProtectedFeature>
        );
      case 'branding':
        return (
          <ProtectedFeature feature="branding">
            <BrandingView />
          </ProtectedFeature>
        );
      case 'aiControls':
        return (
          <ProtectedFeature feature="aiControls">
            <AIControlsView />
          </ProtectedFeature>
        );
      case 'signals':
        return (
          <ProtectedFeature feature="signals">
            <SignalEngineView />
          </ProtectedFeature>
        );
      case 'risks':
        return (
          <ProtectedFeature feature="risks">
            <RiskPredictionView />
          </ProtectedFeature>
        );
      case 'dependencies':
        return (
          <ProtectedFeature feature="dependencies">
            <ReleaseIntelligenceView />
          </ProtectedFeature>
        );
      case 'actionsRequired':
        return (
          <ProtectedFeature feature="actionsRequired">
            <div className="space-y-6">
              <ActionsRequired />
              <IssueRegister />
            </div>
          </ProtectedFeature>
        );
      case 'decisions':
        return (
          <ProtectedFeature feature="decisions">
            <DecisionLog />
          </ProtectedFeature>
        );
      case 'auditTrail':
        return (
          <ProtectedFeature feature="auditTrail">
            <AuditTrailView />
          </ProtectedFeature>
        );
      case 'hitlWorkbench':
        return (
          <ProtectedFeature feature="hitlWorkbench">
            <HITLGovernanceView />
          </ProtectedFeature>
        );
      case 'tenantSettings': {
        const TenantSettings = lazy(() => import('@/components/admin/TenantSettings'));
        return (
          <ProtectedFeature feature="tenantSettings">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>}>
              <TenantSettings />
            </Suspense>
          </ProtectedFeature>
        );
      }
      case 'settings':
        return (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-foreground mb-2">Settings</h2>
              <p>Configuration options coming soon...</p>
            </div>
          </div>
        );
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        activeView={activeView} 
        onViewChange={handleViewChange}
        isOpen={sidebarOpen}
        onOpenChange={setSidebarOpen}
      />
      
      <div className={cn(
        'h-screen flex flex-col transition-all duration-300',
        !isMobile && 'ms-72'
      )}>
        <Header
          isDark={isDark}
          onThemeToggle={() => setTheme(isDark ? 'light' : 'dark')}
          onMenuClick={() => setSidebarOpen(true)}
          onlineUsers={onlineUsers}
          onNavigate={setActiveView}
        />

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {renderContent()}
        </main>
      </div>
      
      <AIChatButton />
    </div>
  );
};

export default Index;
